/**
 * Toast with Undo (board 15 pattern 9). "List archived · Undo". Undo calls the action's own
 * inverse; for actions whose undo must outlive the page, the caller records them in the
 * activity log (built in the phases that need it) and the toast is only a shortcut.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

interface ToastItem { id: number; text: string; undo?: () => void | Promise<void> }
interface ToastApi { show: (text: string, opts?: { undo?: ToastItem['undo']; ms?: number }) => void }

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback<ToastApi['show']>((text, opts = {}) => {
    const id = (seq.current += 1);
    setItems((xs) => [...xs, { id, text, undo: opts.undo }]);
    setTimeout(() => dismiss(id), opts.ms ?? (opts.undo ? 10000 : 4000));
  }, [dismiss]);
  return (
    <Ctx.Provider value={{ show }}>
      {children}
      <div className="ds-toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="ds-toast">
            <span>{t.text}</span>
            {t.undo ? <button type="button" onClick={async () => { dismiss(t.id); await t.undo?.(); }}>Undo</button> : null}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}
